
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { debounce } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';

interface UrlInputProps {
  className?: string;
  onAnalysisComplete?: (analysis: any) => void;
  onLoadingStateChange?: (isLoading: boolean, step: number, message: string) => void;
  disabled?: boolean;
}

// Função para extrair ID e validar URL
const extractPropertyInfo = (url: string) => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    
    if (hostname.includes('chavesnamao.com.br')) {
      const idMatch = url.match(/id-(\d+)/);
      return {
        source: 'chavesnamao',
        id: idMatch?.[1] || null,
        isValid: !!idMatch?.[1]
      };
    }
    
    if (hostname.includes('zapimoveis.com.br')) {
      const idMatch = url.match(/id-(\d+)/);
      return {
        source: 'zapimoveis',
        id: idMatch?.[1] || null,
        isValid: !!idMatch?.[1]
      };
    }
    
    if (hostname.includes('infoimoveis.com.br')) {
      const idMatch = url.match(/\/(\d+)(?:\/|$)/);
      return {
        source: 'infoimoveis',
        id: idMatch?.[1] || null,
        isValid: !!idMatch?.[1]
      };
    }

    return {
      source: null,
      id: null,
      isValid: false
    };
  } catch (e) {
    return {
      source: null,
      id: null,
      isValid: false
    };
  }
};

const UrlInput: React.FC<UrlInputProps> = ({ 
  className = '', 
  onAnalysisComplete,
  onLoadingStateChange,
  disabled = false
}) => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [processingStep, setProcessingStep] = useState(0);
  const [processingMessage, setProcessingMessage] = useState('');
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const navigate = useNavigate();

  // Validação debounced da URL
  const validateUrl = useCallback(
    debounce((value: string) => {
      if (!value) {
        setIsValid(true);
        return;
      }

      const propertyInfo = extractPropertyInfo(value);
      setIsValid(propertyInfo.isValid);
    }, 300),
    []
  );

  const updateLoadingState = (loading: boolean, step: number, message: string) => {
    setIsLoading(loading);
    setProcessingStep(step);
    setProcessingMessage(message);
    
    if (onLoadingStateChange) {
      onLoadingStateChange(loading, step, message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmitTime < 3000) {
      toast.error("Por favor, aguarde alguns segundos antes de enviar outra análise.");
      return;
    }
    setLastSubmitTime(now);
    
    // Validar URL e extrair ID
    const propertyInfo = extractPropertyInfo(url);
    if (!propertyInfo.isValid) {
      setIsValid(false);
      toast.error("URL inválida. Por favor, insira uma URL válida do ZapImóveis, Chaves na Mão ou InfoImóveis.");
      return;
    }
    
    try {
      updateLoadingState(true, 1, 'Analisando imóvel...');
      
      // Chamar a função analyze_property do Supabase
      const { data: analysisResult, error } = await supabase.rpc('analyze_property', {
        p_property_id: propertyInfo.id
      });

      if (error) throw error;
      if (!analysisResult) throw new Error("Não foi possível analisar o imóvel");

      // Armazenar resultado no sessionStorage
      sessionStorage.setItem('propertyAnalysis', JSON.stringify(analysisResult));
      
      // Chamar callback se existir
      if (onAnalysisComplete) {
        onAnalysisComplete(analysisResult);
      }
      
      // Redirecionar para página de relatório
      updateLoadingState(true, 2, 'Gerando relatório...');
      setTimeout(() => {
        updateLoadingState(false, 0, '');
        navigate('/report');
        toast.success("Análise do imóvel concluída com sucesso!");
      }, 1000);
      
    } catch (error: any) {
      console.error("Erro durante a análise:", error);
      toast.error(error.message || "Não foi possível analisar o imóvel. Tente novamente mais tarde.");
      updateLoadingState(false, 0, '');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    validateUrl(newUrl);
  };

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <ExternalLink size={18} />
          </div>
          <input
            type="url"
            placeholder={disabled ? "Sistema temporariamente indisponível" : "Cole aqui o link do imóvel"}
            value={url}
            onChange={handleInputChange}
            className={`w-full rounded-lg py-3 pl-10 pr-20 border ${!isValid ? 'border-red-500' : 'border-gray-300'} text-gray-900 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60`}
            disabled={isLoading || disabled}
          />
          <button
            type="submit"
            disabled={isLoading || disabled}
            className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-3 py-2 transition-all duration-300 flex items-center justify-center ${
              disabled 
                ? 'bg-gray-400 text-white cursor-not-allowed opacity-60' 
                : 'bg-blue-dark text-white hover:bg-opacity-90 disabled:opacity-70'
            }`}
          >
            {isLoading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Search size={20} />
            )}
          </button>
        </div>
      </form>
      
      {isLoading && (
        <div className="mt-4">
          <Alert className="bg-blue-50 border-blue-100">
            <AlertDescription className="flex items-center">
              <Loader2 size={16} className="animate-spin mr-2" />
              <span>{processingMessage}</span>
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
};

export default UrlInput;
